import { DataGrid, GridColDef, GridRowSelectionModel, GridSortModel } from "@mui/x-data-grid";
import { Product, ProductSort, ProductSortField, Supplier } from "../types/api";
import { FC, useMemo, useState } from "react";
import { Paper } from "@mui/material";
import { dataTables } from "src/styles";
import { currencyFormatter } from "src/util";
import TagChips from "./tag-chips";
import CustomGridPagination from "./custom-pagination-actions";
import { ImageViewerModal } from "./modal.image-viewer";

interface Props {
  productData: Product[] | undefined | null | any;
  suppliers: Supplier[];
  pageSize: number;
  totalRows: number;
  currentPage: number;
  onSelectProduct: Function;
  onPageChange: Function;
  onPageSizeChange: Function;
  loading: boolean;
  showChecks?: boolean;
  onRowSelectionModelChange?: (model: GridRowSelectionModel) => void;
  rowSelectionModel?: GridRowSelectionModel;
  /** Current server-side sort (null = default order) and how to change it. */
  sort?: ProductSort | null;
  onSortChange?: (sort: ProductSort | null) => void;
}

// Sorting is done by the server (sortingMode="server"): the grid only ever
// holds one page, so sorting locally would order that page rather than the
// whole list. Only columns the server can sort on are marked sortable.
const toSortModel = (sort: ProductSort | null | undefined): GridSortModel =>
  sort ? [{ field: sort.field, sort: sort.dir }] : [];

const fromSortModel = (model: GridSortModel): ProductSort | null => {
  const first = model[0];
  return first && first.sort
    ? { field: first.field as ProductSortField, dir: first.sort }
    : null;
};

const ProductTable: FC<Props> = (props: Props) => {
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState({ url: "", alt: "" });

  // Stable reference: the grid treats a new sortModel array as a model change
  // and calls onSortModelChange, which would reset the page on every render.
  const sortModel = useMemo(
    () => toSortModel(props.sort),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.sort?.field, props.sort?.dir]
  );

  const safeProductData = Array.isArray(props.productData) ? props.productData : [];
  const productGuidMap: Map<string, Product> = safeProductData.reduce(
    (map: Map<string, Product>, p: Product) => {
      map.set(p.guid!, p);
      return map;
    },
    new Map<string, Product>()
  );

  const handleImageClick = (imageUrl: string, imageAlt: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent row click
    setSelectedImage({ url: imageUrl, alt: imageAlt });
    setImageModalOpen(true);
  };

  const handleCloseModal = () => {
    setImageModalOpen(false);
  };


  const columns: GridColDef[] = [
    { field: "id" },
    { field: "guid" },
    {
      field: "image",
      headerName: "Image",
      sortable: false,
      minWidth: 80,
      align: "center",
      renderCell: (params) => (
        <img
          src={params.value[0]}
          height={40}
          width={40}
          alt={params.value[1]}
          onClick={(e) => handleImageClick(params.value[0], params.value[1], e)}
          style={{ cursor: "pointer" }}
          onError={(e) => {
            (e.target as HTMLImageElement).src = "https://upload.wikimedia.org/wikipedia/commons/1/14/No_Image_Available.jpg";
          }}
        />
      ),
    },
    { field: "name", headerName: "Name", minWidth: 400, flex: 2 },
    { field: "supplier", headerName: "Supplier", minWidth: 200, flex: 1, sortable: false },
    {
      field: "tags",
      headerName: "Tags",
      minWidth: 400,
      flex: 2,
      sortable: false,
      renderCell: (params) => <TagChips tags={params.value} />,
    },
    { field: "productType", headerName: "Type", minWidth: 100, flex: 0.5 },
    {
      field: "available",
      headerName: "Available",
      minWidth: 150,
      flex: 0.5,
      sortable: false,
      renderCell: (params) => {
        const n = params.value[0];
        const nV = params.value[1];
        const color = n === 0 ? "orange" : "green";
        const suffix = nV !== 1 ? "s" : "";
        return (
          <div>
            <span style={{ color: color }}>{n}</span> in {nV} variant{suffix}
          </div>
        );
      },
    },
    {
      field: "priceRange",
      headerName: "Price",
      minWidth: 150,
      flex: 0.6,
      sortable: false,
      align: "right",
      renderCell: (params) => {
        const minPrice = params.value[0];
        const maxPrice = params.value[1];

        // return `${minPrice}-${maxPrice}`
        if (minPrice === maxPrice) {
          return currencyFormatter.format(minPrice / 100);
        }

        return `${currencyFormatter.format(
          minPrice / 100
        )}-${currencyFormatter.format(maxPrice / 100)}`;
      },
    },
    { field: "location", headerName: "Location", minWidth: 150, flex: 1 },
  ];

  const rows = safeProductData.map((p: Product) => {
    const stockOnHand = p.variants && p.variants.length > 0 
      ? p.variants.map((v) => v.stockOnHand).reduce((a, b) => a + b, 0)
      : 0;

    const _fixedUri = p.variants?.[0]?.imageUrls?.[0] || "https://upload.wikimedia.org/wikipedia/commons/1/14/No_Image_Available.jpg";
      

    const prices: number[] = p.variants && p.variants.length > 0 
      ? p.variants.map((v) => +v.retailPrice)
      : [0];

    return {
      id: p.id,
      guid: p.guid,
      image: [_fixedUri, p.name],
      name: p.name,
      supplier: props.suppliers.find((s: Supplier) => s.guid === p.supplierGuid)?.name || "Unknown",
      tags: p.tags,
      productType: p.productType,
      available: [stockOnHand, p.variants ? p.variants.length : 0],
      priceRange: [Math.min(...prices), Math.max(...prices)],
      location: p.location,
    };
  });

  return (
    <>
      <Paper
        component="form"
        sx={{
          p: "2px 4px",
          display: "flex",
          alignItems: "center",
        }}
      >
        <DataGrid
          loading={props.loading}
          rows={rows}
          columns={columns}
          rowCount={props.totalRows}
          paginationMode="server"
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={(model) => props.onSortChange?.(fromSortModel(model))}
          density="compact"
          onRowClick={(colParams) => {
            const product = productGuidMap.get(colParams.row.guid);
            if (product) {
              props.onSelectProduct(product);
            } else {
              console.error('Product not found for guid:', colParams.row.guid);
            }
          }}
          paginationModel={{ page: props.currentPage, pageSize: props.pageSize }}
          onPaginationModelChange={(model) => {
            props.onPageChange(model.page);
            props.onPageSizeChange(model.pageSize);
          }}
          checkboxSelection={props.showChecks || false}
          onRowSelectionModelChange={props.onRowSelectionModelChange ? (model) => {
              props.onRowSelectionModelChange!(model);
          } : undefined}
          rowSelectionModel={props.rowSelectionModel}
          columnVisibilityModel={{
            id: false,
            guid: false
          }}
          slots={{
            pagination: CustomGridPagination,
          }}
          sx={dataTables}
        />
      </Paper>
      <ImageViewerModal
        open={imageModalOpen}
        imageUrl={selectedImage.url}
        imageAlt={selectedImage.alt}
        onClose={handleCloseModal}
      />
    </>
  );
};

export default ProductTable;
